import { useState } from 'react';
import { collection, orderBy, query, where } from 'firebase/firestore';
import { callable, db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useDocs } from '@/lib/useDocs';
import { messageForAuthError } from '@/lib/authErrors';
import { matchStatusLabel } from '@/lib/format';
import type { Match, Opportunity } from '@/types/domain';
import { strings } from '@/strings';

const orgRespond = callable<{ matchId: string; accepted: boolean }, { matchId: string; status: string }>('orgRespond');

/** Nothing before candidate acceptance is ever rendered here. */
const VISIBLE = ['candidate_accepted', 'org_accepted', 'org_declined', 'active', 'completed', 'ended_early'];

export function OrgMatchesPage() {
  const { claims } = useAuth();
  const orgId = String(claims.orgId || '');

  const { rows: matches, loading, error } = useDocs<Match>(
    orgId ? query(collection(db, 'matches'), where('orgId', '==', orgId), orderBy('createdAt', 'desc')) : null,
    [orgId]
  );
  const { rows: opportunities } = useDocs<Opportunity>(
    orgId ? query(collection(db, 'opportunities'), where('orgId', '==', orgId), orderBy('createdAt', 'desc')) : null,
    [orgId]
  );
  const byId = new Map(opportunities.map((o) => [o.id, o]));

  const [message, setMessage] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Belt and braces: the rules refuse to serve a `proposed` match and the
  // document carries no `disclosed` field until acceptance, but the screen
  // filters too — three independent reasons an organization cannot see someone
  // who has not agreed.
  const visible = matches.filter((m) => VISIBLE.includes(m.status) && m.disclosed);

  async function answer(matchId: string, accepted: boolean) {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    setMessage(null);
    try {
      await orgRespond({ matchId, accepted });
      setMessage(strings.matchAnswered);
    } catch (err) {
      setProblem(messageForAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <h1 className="text-xl font-bold text-primary">{strings.orgMatchesTitle}</h1>
      {loading ? <p className="text-muted">{strings.loading}</p> : null}
      {error ? <p role="alert" className="text-danger">{strings.loadFailed}</p> : null}
      {!loading && !error && visible.length === 0 ? <p className="text-muted">{strings.orgMatchesEmpty}</p> : null}

      <ul className="flex flex-col gap-4">
        {visible.map((match) => (
          <li key={match.id} className="flex flex-col gap-2 rounded-lg border border-border-subtle bg-white p-4">
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="text-lg font-semibold text-ink">{match.disclosed?.name}</span>
              <span className="text-sm text-muted">{match.disclosed?.currentRole}</span>
              <span className="text-sm text-muted">{byId.get(match.opportunityId)?.title || ''}</span>
            </div>
            <p className="text-ink">{match.disclosed?.bio}</p>
            <p className="text-sm text-muted">
              {strings.fieldSupervisionYears}: {match.disclosed?.supervisionYears ?? 0} — {strings.fieldHours}: {match.disclosed?.availability?.hoursPerWeek ?? 0}
            </p>

            <details>
              <summary className="cursor-pointer text-sm text-primary">{strings.matchWhyTitle}</summary>
              <ul className="flex flex-col gap-1 pt-2 text-xs text-muted">
                {(match.scores?.reasons || []).map((reason) => <li key={reason}>{reason}</li>)}
              </ul>
            </details>

            {match.status === 'candidate_accepted' ? (
              <div className="flex gap-2">
                <button type="button" disabled={busy} onClick={() => answer(match.id, true)}
                  className="min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50">
                  {strings.orgMatchAccept}
                </button>
                <button type="button" disabled={busy} onClick={() => answer(match.id, false)}
                  className="min-h-11 rounded-md border border-border-subtle bg-white px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-50">
                  {strings.orgMatchDecline}
                </button>
              </div>
            ) : (
              <span className="text-sm text-muted">{matchStatusLabel(match.status)}</span>
            )}
          </li>
        ))}
      </ul>

      {message ? <p className="text-sm text-success">{message}</p> : null}
      {problem ? <p role="alert" className="text-sm text-danger">{problem}</p> : null}
    </div>
  );
}
